import { useState, useEffect, useRef } from "react";
import { Sun, Cloud, CloudRain, Wind, Droplets, Thermometer, Search, MapPin, Phone, MessageSquare, X, ExternalLink } from "lucide-react";

// =============================================
// PAGE MÉTÉO DÉGUISÉE — PANIC BUTTON
// Objectif : victime consulte une page météo anodine.
// Trigger secret : cliquer 3× sur le soleil OU taper "urgence" / "sos" dans la recherche.
// Panneau d'urgence affiche : 3919, 17, 114, 115, 119, 3020, 08VICTIMES
// Bouton "Quitter" redirige vers google.com sans historique
// =============================================

const CITIES = [
  { name: "Paris", temp: 12, cond: "nuageux", icon: "cloud", humid: 74, wind: 18, forecast: [{ d: "Demain", t: 14, i: "sun" }, { d: "Mer.", t: 11, i: "rain" }, { d: "Jeu.", t: 9, i: "rain" }, { d: "Ven.", t: 13, i: "cloud" }] },
  { name: "Lyon", temp: 9, cond: "pluvieux", icon: "rain", humid: 88, wind: 22, forecast: [{ d: "Demain", t: 10, i: "rain" }, { d: "Mer.", t: 12, i: "cloud" }, { d: "Jeu.", t: 15, i: "sun" }, { d: "Ven.", t: 13, i: "sun" }] },
  { name: "Marseille", temp: 17, cond: "ensoleillé", icon: "sun", humid: 52, wind: 28, forecast: [{ d: "Demain", t: 19, i: "sun" }, { d: "Mer.", t: 20, i: "sun" }, { d: "Jeu.", t: 18, i: "cloud" }, { d: "Ven.", t: 16, i: "cloud" }] },
  { name: "Toulouse", temp: 14, cond: "nuageux", icon: "cloud", humid: 65, wind: 12, forecast: [{ d: "Demain", t: 16, i: "sun" }, { d: "Mer.", t: 18, i: "sun" }, { d: "Jeu.", t: 14, i: "cloud" }, { d: "Ven.", t: 12, i: "rain" }] },
  { name: "Bordeaux", temp: 11, cond: "pluvieux", icon: "rain", humid: 92, wind: 24, forecast: [{ d: "Demain", t: 12, i: "cloud" }, { d: "Mer.", t: 14, i: "sun" }, { d: "Jeu.", t: 15, i: "sun" }, { d: "Ven.", t: 13, i: "cloud" }] },
  { name: "Lille", temp: 7, cond: "pluvieux", icon: "rain", humid: 95, wind: 32, forecast: [{ d: "Demain", t: 8, i: "rain" }, { d: "Mer.", t: 9, i: "cloud" }, { d: "Jeu.", t: 10, i: "sun" }, { d: "Ven.", t: 8, i: "rain" }] },
];

const EMERGENCY = [
  { code: "3919", label: "Violences Femmes Info", desc: "Anonyme et gratuit 24h/24", type: "tel", sms: false },
  { code: "17", label: "Police / Gendarmerie", desc: "Urgence immédiate", type: "tel", sms: false },
  { code: "114", label: "SMS Urgence (sourds/muets)", desc: "Envoyer SMS quand appeler est risqué", type: "tel", sms: true },
  { code: "119", label: "Enfance en danger", desc: "24h/24, gratuit, anonyme", type: "tel", sms: false },
  { code: "115", label: "SAMU social / Hébergement", desc: "Mise à l'abri sans domicile", type: "tel", sms: false },
  { code: "3020", label: "Non au harcèlement", desc: "Écoles + établissements", type: "tel", sms: false },
];

const Icon = ({ name, size = 48, cls = "" }) => {
  const props = { size, className: cls };
  if (name === "sun") return <Sun {...props} />;
  if (name === "rain") return <CloudRain {...props} />;
  return <Cloud {...props} />;
};

export default function PanicPage() {
  const [city, setCity] = useState(CITIES[0]);
  const [query, setQuery] = useState("");
  const [showPanic, setShowPanic] = useState(false);
  const clickCount = useRef(0);
  const clickTimer = useRef(null);

  // Trigger secret 1 : query contient "urgence" ou "sos" ou "help" ou "3919"
  useEffect(() => {
    const q = query.toLowerCase().trim();
    if (["urgence", "sos", "help", "aide", "3919", "police"].some(k => q.includes(k))) {
      setShowPanic(true);
      setQuery("");
    }
  }, [query]);

  // Trigger secret 2 : 3 clics rapides sur le soleil
  const handleSecretClick = () => {
    clickCount.current += 1;
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => { clickCount.current = 0; }, 1500);
    if (clickCount.current >= 3) {
      setShowPanic(true);
      clickCount.current = 0;
    }
  };

  // Bouton "Quitter" — redirige vers google.com et remplace historique
  const quickExit = () => {
    try {
      window.location.replace("https://www.google.fr/search?q=météo+aujourd%27hui");
    } catch {
      window.location.href = "https://www.google.fr";
    }
  };

  // Trigger secret 3 : touche Échap ouvre le panneau
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !showPanic) {
        setShowPanic(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPanic]);

  const filtered = query
    ? CITIES.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
    : CITIES;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-blue-50 to-white text-slate-900" data-testid="panic-weather-page">
      {/* Header type Météo France */}
      <header className="bg-blue-600 text-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleSecretClick}
              className="cursor-pointer"
              aria-label="Logo météo"
              data-testid="panic-secret-sun"
            >
              <Sun className="w-8 h-8 text-yellow-300" />
            </button>
            <div>
              <div className="text-lg font-bold leading-none">Météo Direct</div>
              <div className="text-xs opacity-80">Prévisions locales — France</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-5 text-sm">
            <a href="#" className="hover:underline">Accueil</a>
            <a href="#" className="hover:underline">Cartes</a>
            <a href="#" className="hover:underline">Vidéos</a>
            <a href="#" className="hover:underline">Alertes</a>
          </nav>
        </div>
      </header>

      {/* Barre de recherche */}
      <section className="max-w-6xl mx-auto px-4 pt-6">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une ville…"
            className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none bg-white"
            data-testid="panic-search-input"
          />
        </div>
      </section>

      {/* Ville principale */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-md p-6 md:p-8 border border-slate-100">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <MapPin className="w-4 h-4" /> {city.name}, France
              </div>
              <div className="mt-2 flex items-end gap-4">
                <div className="text-6xl md:text-7xl font-black text-slate-800 tabular-nums">{city.temp}°</div>
                <Icon name={city.icon} size={72} cls="text-blue-500" />
              </div>
              <div className="text-lg text-slate-600 capitalize mt-2">{city.cond}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2"><Droplets className="w-4 h-4 text-blue-500" /> Humidité <b>{city.humid}%</b></div>
              <div className="flex items-center gap-2"><Wind className="w-4 h-4 text-blue-500" /> Vent <b>{city.wind} km/h</b></div>
              <div className="flex items-center gap-2"><Thermometer className="w-4 h-4 text-blue-500" /> Ressenti <b>{city.temp - 2}°</b></div>
              <div className="flex items-center gap-2"><Cloud className="w-4 h-4 text-blue-500" /> Pression <b>1013 hPa</b></div>
            </div>
          </div>

          {/* Prévisions 4 jours */}
          <div className="mt-8 grid grid-cols-4 gap-3">
            {city.forecast.map((f, i) => (
              <div key={i} className="text-center p-3 rounded-xl bg-blue-50/60 border border-blue-100">
                <div className="text-xs text-slate-500 uppercase tracking-wide">{f.d}</div>
                <div className="my-2 flex justify-center"><Icon name={f.i} size={32} cls="text-blue-500" /></div>
                <div className="font-bold text-slate-800">{f.t}°</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grille villes */}
      <section className="max-w-6xl mx-auto px-4 pb-10">
        <h2 className="text-slate-700 font-bold mb-3">Autres villes</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {filtered.filter(c => c.name !== city.name).map((c) => (
            <button
              key={c.name}
              onClick={() => setCity(c)}
              className="text-left p-3 rounded-xl bg-white border border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all"
              data-testid={`panic-city-${c.name.toLowerCase()}`}
            >
              <div className="text-xs text-slate-500">{c.name}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xl font-black text-slate-800">{c.temp}°</span>
                <Icon name={c.icon} size={22} cls="text-blue-500" />
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-sm text-slate-500 italic">Aucune ville trouvée.</div>
          )}
        </div>
      </section>

      <footer className="bg-slate-800 text-slate-300 py-6 mt-6">
        <div className="max-w-6xl mx-auto px-4 text-xs flex justify-between flex-wrap gap-2">
          <span>© Météo Direct — Données indicatives</span>
          <span className="opacity-60">Mise à jour toutes les 3h · Prévisions ©</span>
        </div>
      </footer>

      {/* Panneau d'URGENCE — révélé par trigger secret */}
      {showPanic && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="panic-overlay"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden">
            <div className="bg-red-600 text-white p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-90">Aide immédiate</div>
                <div className="text-xl font-black">Numéros d'urgence · gratuits · anonymes</div>
              </div>
              <button
                onClick={() => setShowPanic(false)}
                className="p-2 rounded-full hover:bg-red-700"
                aria-label="Fermer"
                data-testid="panic-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              {EMERGENCY.map((e) => (
                <a
                  key={e.code}
                  href={e.sms ? `sms:${e.code}` : `tel:${e.code}`}
                  className="flex items-center gap-4 p-3 rounded-xl border-2 border-slate-200 hover:border-red-500 hover:bg-red-50 transition-all"
                  data-testid={`panic-num-${e.code}`}
                >
                  <div className="w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center font-black text-lg flex-shrink-0">
                    {e.code}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{e.label}</div>
                    <div className="text-xs text-slate-600">{e.desc}</div>
                  </div>
                  {e.sms ? <MessageSquare className="w-5 h-5 text-red-500" /> : <Phone className="w-5 h-5 text-red-500" />}
                </a>
              ))}

              <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <b>Astuce sécurité :</b> le bouton <b>« Quitter »</b> ci-dessous remplace immédiatement cette page par une recherche Google. Pensez aussi à effacer manuellement l'historique de votre navigateur si un partage d'appareil vous met en danger.
              </div>

              <a
                href="https://arretonslesviolences.gouv.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm"
                data-testid="panic-gov-link"
              >
                <span>Ressources officielles · arretonslesviolences.gouv.fr</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="p-4 bg-slate-50 flex flex-col md:flex-row gap-2 justify-between items-stretch md:items-center">
              <button
                onClick={() => setShowPanic(false)}
                className="px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 text-sm"
                data-testid="panic-back-weather"
              >
                Revenir à la météo
              </button>
              <button
                onClick={quickExit}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 text-sm"
                data-testid="panic-quick-exit"
              >
                Quitter immédiatement (Google)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
